import styled from 'styled-components'
import useTranslation from '../../hooks/useTranslation'
import BackButton from '../generic/BackButton'
import ProgressTracker from '../generic/ProgressTracker'

const Header = styled.div`
    position: sticky;
    top: 0;
    z-index: 100;
    background: ${({ theme }) => theme.form.background};
    border: 1px solid ${({ theme }) => theme.form.border};
    border-radius: 1rem;
    padding: 0.75rem 1rem;
    margin-bottom: 1rem;
`

const TopRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.4rem;
`

const TitleBlock = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
    overflow: hidden;
`

const ButtonRow = styled.div`
    display: flex;
    gap: 0.5rem;
    flex-shrink: 0;
`

const FieldTitle = styled.span`
    font-weight: 600;
    font-size: 1rem;
    color: ${({ theme }) => theme.font.color};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const FieldSubtitle = styled.span`
    font-size: 0.82rem;
    color: ${({ theme }) => theme.font.label};
    white-space: nowrap;
`

const ActionButton = styled.button`
    display: flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.35rem 0.9rem;
    font-size: 0.82rem;
    font-weight: 600;
    white-space: nowrap;
    border: none;
    border-radius: 999px;
    cursor: pointer;
    background: ${({ theme, $variant }) => $variant === 'secondary' ? theme.btn.secondary : theme.btn.primary};
    color: ${({ theme }) => theme.btn.font};
    &:hover { opacity: 0.88; }
    &:disabled { opacity: 0.45; cursor: not-allowed; }
`

const CompletedBadge = styled.span`
    font-size: 0.82rem;
    font-weight: 700;
    color: ${({ theme }) => theme.btn.primary};
    padding: 0.35rem 0.9rem;
    border-radius: 999px;
    border: 1.5px solid ${({ theme }) => theme.btn.primary};
    white-space: nowrap;
`

const renderElement = (el, doc) => {
    const value = doc[el.value]
    if (!value) return null
    if (el.scale === 'title') return <FieldTitle key={el.value}>{value}</FieldTitle>
    const parts = [el.prefix, value, el.suffix].filter(Boolean)
    return <FieldSubtitle key={el.value}>{parts.join(' ')}</FieldSubtitle>
}

const ProcessHeader = ({ doc, lines = [], section, onAction, backOverride, progress, viewKey }) => {
    const { t } = useTranslation()

    // `progress` on the section is a flag; the fields it reads live at the root
    // of the view, next to `items`, because progress is one concern.
    const { elements = [], progress: showProgress, buttons = [], completedField, completedLabel, completeRequires } = section
    const progressValue = doc[progress?.completed] ?? 0
    const progressTotal = doc[progress?.total]     ?? 0
    const isCompleted   = completedField ? !!doc[completedField] : false

    // Enforced completion: when the view declares completeRequires {field,value},
    // the action buttons stay disabled until every line meets it (e.g. all lines
    // dropped before a transfer can be finalized).
    const canComplete = !completeRequires
        || (lines.length > 0 && lines.every(l => l[completeRequires.field] === completeRequires.value))

    return (
        <Header>
            <TopRow>
                <TitleBlock>
                    <BackButton config={{ backOverride }} />
                    {elements.map(el => renderElement(el, doc))}
                </TitleBlock>

                <ButtonRow>
                    {isCompleted
                        ? <CompletedBadge data-testid={`${viewKey}.${section.id}.completed`}>{t(completedLabel)}</CompletedBadge>
                        : buttons.map((btn, i) => (
                            <ActionButton
                                key={i}
                                type="button"
                                $variant={btn.variant}
                                data-testid={btn.id}
                                disabled={!canComplete}
                                title={!canComplete && completeRequires?.hint ? t(completeRequires.hint) : undefined}
                                onClick={() => canComplete && onAction(btn.effects)}
                            >
                                {t(btn.label)}
                            </ActionButton>
                        ))
                    }
                </ButtonRow>
            </TopRow>

            {showProgress && (
                <ProgressTracker value={progressValue} total={progressTotal} size="sm" />
            )}
        </Header>
    )
}

export default ProcessHeader
